import React, { useEffect } from "react";
import { formatNumber } from "@/util/sums";
import ColFormValueSlider from "./ColFormValueSlider";
import { useController, useFormContext, useWatch } from "react-hook-form";
import clamp from "@/util/clamp";
import ControlledFormField from "@/model/interface/ControllerRangeSlider";
import { CalculatorField } from "@/model/interface/CalculatorTypes";

const calculateMonthlyRepayment = (depsArray: number[]) => {
  const [homeValue] = depsArray;

  const minAmount = (homeValue * 0.8 + homeValue * 0.8 * 0.01 * 30) / 360 || 1400;
  const maxAmount = (homeValue * 0.9 + homeValue * 0.9 * 0.1 * 30) / 360 || 25000;
  const calcValue = (homeValue * 0.9 + homeValue * 0.9 * 0.03 * 30) / 360;

  return {
    defaultValue: 10000,
    minAmount,
    maxAmount,
    value: clamp(calcValue, minAmount, maxAmount),
  };
};

const fieldName = CalculatorField.MonthlyRepayment;
const fieldWatched = CalculatorField.HomeValue;

const MortgageMonthlyRepay: React.FC<ControlledFormField> = ({ fieldPrefix }) => {
  const targetName = [fieldPrefix, fieldName].filter(Boolean).join(".");
  const dependency = [fieldPrefix, fieldWatched].filter(Boolean).join(".");

  const { control, setValue } = useFormContext();
  const depsArray = useWatch({ control, name: [dependency] });

  const { value, defaultValue, ...minMaxParams } = calculateMonthlyRepayment(depsArray);

  const { field } = useController({
    control,
    name: targetName,
    defaultValue,
  });

  useEffect(() => {
    if (!value) return;
    setValue(targetName, value);
  }, [depsArray]);

  return (
    <ColFormValueSlider
      title="Estimated monthly mortgage repayment value"
      step={50}
      value={field.value}
      {...minMaxParams}
      rightColumnValue={field.value}
      rightColumnFormatFunction={(value) => `$${formatNumber(value, 0, true)}`}
      onFinalChange={field.onChange}
      onChange={field.onChange}
    />
  );
};

export default MortgageMonthlyRepay;
